import { useAuth } from "@dl/web-core-js/auth";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Badge, Button } from "@dl/web-core-js/ui";
import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatNumber } from "../../../api/manufacturing";
import { ProductionOrderClient, type MaterialAdjustmentRowDTO, type ProductionOrderDTO } from "../../../api/production";
import { UnplannedMaterialDialog } from "./LotDialogs";
import { when } from "./lotModel";

/**
 * What the lot really used beyond the estimate, newest first: corrections made
 * from the office ("Adjust used") and panels counted at the saw. Append-only:
 * a mistake is corrected by another row (negative), which shows here too.
 * Once a material is counted, its consumption follows the counts. A material
 * the confirmed production BOM did not list is added here as unplanned.
 */
export function MaterialLogTab({
  lot,
  canAddUnplanned,
  onChanged,
}: {
  lot: ProductionOrderDTO;
  /** Released or later, and allowed to write. */
  canAddUnplanned: boolean;
  onChanged: () => Promise<void>;
}) {
  const { t, i18n } = useTranslation();
  const { http } = useAuth();
  const lotId = lot.id;
  const [adding, setAdding] = useState(false);
  const log = useQuery({
    queryKey: ["production", "production-order", lotId, "material-log"],
    queryFn: () => new ProductionOrderClient(http).materialLog(lotId),
  });

  const cols = useMemo(() => {
    const c = defineColumns<MaterialAdjustmentRowDTO>();
    return [
      c.text("created_at", (row) => when(row.created_at, i18n.language), { headerKey: "lot.history.when", priority: 1 }),
      c.text("part_number", (row) => row.part_number, { headerKey: "column.part.number", priority: 1 }),
      c.custom(
        "kind",
        (row) => row.kind,
        (row) => (
          <Badge variant={row.kind === "COUNTED" ? "info" : "neutral"} size="sm">
            {row.kind === "COUNTED" ? t("lot.material-log.counted", "Counted on the floor") : t("lot.material-log.adjusted", "Adjusted at the office")}
          </Badge>
        ),
        { headerKey: "lot.material-log.kind", priority: 1 },
      ),
      c.custom(
        "quantity",
        (row) => Number(row.quantity),
        (row) => (
          <span className={Number(row.quantity) < 0 ? "tabular-nums text-risk" : "tabular-nums"}>
            {Number(row.quantity) > 0 ? "+" : ""}
            {formatNumber(row.quantity)}
          </span>
        ),
        { headerKey: "lot.material-log.quantity", priority: 1, align: "right" },
      ),
      c.text("where", (row) => [row.component_code, row.process_name, row.station_code].filter(Boolean).join(" · "), {
        headerKey: "lot.material-log.where",
        priority: 2,
      }),
      c.text("who", (row) => row.operator_name ?? row.created_by_name ?? "", { headerKey: "lot.material-log.who", priority: 2 }),
      c.text("note", (row) => row.note ?? "", { headerKey: "column.note", priority: 3 }),
    ];
  }, [t, i18n.language]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <DataTable<MaterialAdjustmentRowDTO>
        data={log.data ?? []}
        columns={cols}
        getRowId={(row) => String(row.id)}
        loading={log.isLoading}
        toolbarExtra={
          canAddUnplanned ? (
            <Button size="sm" variant="secondary" icon={<Plus className="size-4" />} onClick={() => setAdding(true)}>
              {t("lot.unplanned.add", "Add unplanned material")}
            </Button>
          ) : undefined
        }
        emptyMessage={t("lot.material-log.empty", "Nothing adjusted or counted yet: the lot consumes what its bill estimates.")}
      />
      {adding && (
        <UnplannedMaterialDialog
          lot={lot}
          onClose={() => setAdding(false)}
          onDone={async () => {
            setAdding(false);
            await onChanged();
          }}
        />
      )}
    </div>
  );
}
